import React, { useCallback } from "react";
import { IconButton, Skeleton, Stack, SvgIconTypeMap, Typography } from "@mui/material";
import ReactHtmlParser from "html-react-parser";

import { ReactComponent as InviteIcon } from "assets/icons/invite-user.svg";
import { ReactComponent as ContactIcon } from "assets/icons/contact.svg";
import { ReactComponent as CharacteristicIcon } from "assets/icons/update-characteristics.svg";
import { ReactComponent as NoteIcon } from "assets/icons/note.svg";
import { ReactComponent as FileIcon } from "assets/icons/file.svg";
import { ReactComponent as AnswerIcon } from "assets/icons/answer.svg";
import { ReactComponent as FolderIcon } from "assets/icons/folder-activity.svg";
import { ReactComponent as QuestionIcon } from "assets/icons/question.svg";
import { ReactComponent as EmailIcon } from "assets/icons/email.svg";
import { ReactComponent as FormIcon } from "assets/icons/form-activity.svg";
import { ReactComponent as TaskIcon } from "assets/icons/task-activity.svg";
import { ReactComponent as ErrorIcon } from "assets/icons/error-activity.svg";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { ComponentsHashType, INotification, NotificationAction } from "store/types";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { PaperAndPen } from "assets/material-custom-icons";
import { OverridableComponent } from "@mui/material/OverridableComponent";

export enum NotificationActionCollection {
  NOTE = "note",
  TASK = "task",
  FORM = "form",
  FOLDER = "folder",
  FILE = "file",
  QUESTION = "question",
  EMAIL = "incoming_email",
  COMMENT = "comment",
  REPLY = "reply",
  ANSWER = "answer",
  // CRUNCHBASE = "crunchbase",
  INVITE = "invite",
  // DATA = "monitoring_data",
  // COMMITMENT = "commitment",
  CONTACT = "contact",
  CHARACTERISTIC = "characteristic"
}

const actionIconsHash: ComponentsHashType<NotificationActionCollection> = {
  [NotificationActionCollection.FORM]: FormIcon,
  [NotificationActionCollection.NOTE]: NoteIcon,
  [NotificationActionCollection.TASK]: TaskIcon,
  [NotificationActionCollection.FILE]: FileIcon,
  [NotificationActionCollection.FOLDER]: FolderIcon,
  [NotificationActionCollection.QUESTION]: QuestionIcon,
  [NotificationActionCollection.EMAIL]: EmailIcon,
  [NotificationActionCollection.COMMENT]: AnswerIcon,
  [NotificationActionCollection.REPLY]: AnswerIcon,
  [NotificationActionCollection.ANSWER]: AnswerIcon,
  // [NotificationActionCollection.CRUNCHBASE]: CorporateFareIcon,
  [NotificationActionCollection.INVITE]: InviteIcon,
  // [NotificationActionCollection.DATA]: DataIcon,
  // [NotificationActionCollection.COMM/ITMENT]: CommitmentIcon,
  [NotificationActionCollection.CONTACT]: ContactIcon,
  [NotificationActionCollection.CHARACTERISTIC]: CharacteristicIcon
};

const emptyStateIcons: {
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
    muiName: string;
  };
}[] = [
  {
    icon: ListAltIcon
  },
  {
    icon: PaperAndPen
  },
  {
    icon: FolderOutlinedIcon
  }
];

const entityColor: Partial<Record<NotificationActionCollection, string>> = {
  form: "#F4D9C6",
  file: "#D3ECF0",
  note: "#D5F8DA",
  comment: "#faeec7",
  reply: "#faeec7",
  folder: "#D2DCFF",
  incoming_email: "#E5D3F0",
  task: "#DAD7CD",
  question: "#6C9A8B",
  answer: "#D9CAB3",
  invite: "#FAE9C7",
  contact: "#D8D8D8",
  characteristic: "#D2E1F2"
};

interface IGeneralProps {
  activity: INotification[];
  loading: boolean;
}

export const General: React.FC<IGeneralProps> = ({ activity, loading }) => {
  const getIcon = useCallback((action: NotificationAction): JSX.Element => {
    if (!actionIconsHash.hasOwnProperty(action)) {
      return <ErrorIcon style={{ color: "#0a3142", width: "26px", height: "26px" }} />;
    } else {
      const Icon = actionIconsHash[action as NotificationActionCollection];
      return <Icon style={{ color: "#0a3142", width: "26px", height: "26px" }} />;
    }
  }, []);
  return (
    <>
      {loading ? (
        <Skeleton variant="rounded" width="100%" height="100%" sx={{ flexGrow: 1, maxHeight: 160, minHeight: 160 }} />
      ) : activity.length ? (
        <Stack sx={{ gap: "10px", maxHeight: 160, minHeight: 160, overflow: "auto" }}>
          {activity.map((notification, index) => {
            return (
              <Stack key={index} direction="row">
                <Stack
                  direction="row"
                  sx={{ gap: "12px", p: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }}
                >
                  <IconButton
                    sx={{
                      backgroundColor: !actionIconsHash.hasOwnProperty(notification.action)
                        ? "#E0E0E0"
                        : entityColor[notification.action as NotificationActionCollection],
                      height: 24,
                      width: 24,
                      padding: 0,
                      "&:hover": {
                        backgroundColor: !actionIconsHash.hasOwnProperty(notification.action)
                          ? "#E0E0E0"
                          : entityColor[notification.action as NotificationActionCollection]
                      }
                    }}
                  >
                    {getIcon(notification.action)}
                  </IconButton>
                  <MaterialTooltip placement="top" isShow title={ReactHtmlParser(notification.text)}>
                    <p style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {ReactHtmlParser(notification.text)}
                    </p>
                  </MaterialTooltip>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            bgcolor: "#F6F8FC",
            // flexGrow: 1,
            p: "30px 10px"
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
            {emptyStateIcons.map((item, index) => {
              return <item.icon key={index} sx={{ color: "#92A7BC", fontSize: 32 }} />;
            })}
          </Stack>
          <Typography
            sx={{ fontSize: "14px", color: "#92A7BC", fontWeight: 400, lineHeight: "16px", textAlign: "center" }}
          >
            You don’t have any activity yet.
            <br />
            Start by adding a new form, note, request or a folder.
          </Typography>
        </Stack>
      )}
    </>
  );
};
